package com.zi.support.shared

/**
 * Kanban board vocabulary shared by web, Android and iOS. Firestore layout:
 * `kanbanProjects/{projectId}` with tasks in `kanbanProjects/{projectId}/kanbanTasks/{taskId}`.
 */
object KanbanPolicy {
    const val PROJECTS: String = "kanbanProjects"
    const val TASKS: String = "kanbanTasks"

    val projectStatuses: List<String> = listOf("idea", "backlog", "in_progress", "paused", "done", "cancelled")
    val taskStatuses: List<String> = listOf("todo", "in_progress", "paused", "review", "done")
    val priorities: List<String> = listOf("low", "medium", "high", "critical")

    fun statusLabel(status: String): String = when (status) {
        "idea" -> "Ideas"
        "backlog" -> "Backlog"
        "in_progress" -> "In progress"
        "paused" -> "Paused"
        "done" -> "Done"
        "cancelled" -> "Cancelled"
        "todo" -> "To do"
        "review" -> "Review"
        else -> status.replace('_', ' ').replaceFirstChar { it.uppercase() }
    }

    fun priorityLabel(priority: String): String = priority.replaceFirstChar { it.uppercase() }

    /**
     * Project documents are named after their title so they read naturally in the Firebase console.
     * `/` is a path separator in Firestore, so it becomes `-`; duplicates get " (2)", " (3)"…
     * Keep in sync with functions/scripts/rename-kanban-project-ids.js.
     */
    fun projectDocumentId(title: String, taken: Collection<String>): String {
        var base = title.trim().replace('/', '-').replace(Regex("\\s+"), " ").take(100).trim()
        if (base.isEmpty() || base.all { it == '.' } || (base.startsWith("__") && base.endsWith("__"))) base = "Project $base".trim()
        if (base !in taken) return base
        var n = 2
        while ("$base ($n)" in taken) n++
        return "$base ($n)"
    }

    /**
     * Task timer: `timerStartedAt` is set when a task enters in_progress, `timerStoppedAt` when it leaves.
     * Moving paused → in_progress "resume"s: the time so far goes into `timerBankedMs` and a new spell starts.
     * Returns "start", "resume", "stop" or null for a status move.
     */
    fun timerChange(from: String, to: String): String? = when {
        from == to -> null
        to == "in_progress" -> if (from == "paused") "resume" else "start"
        from == "in_progress" -> "stop"
        else -> null
    }

    const val WORK_START_HOUR: Int = 7
    const val WORK_END_HOUR: Int = 17

    /**
     * Milliseconds between [startMs] and [endMs] that fall inside 07:00–17:00 local time on working days, so the timer
     * pauses overnight, on weekends and on South African public holidays. [offsetMinutes] is the device's UTC offset.
     * ponytail: one offset for the whole span, so a DST switch mid-task is off by an hour.
     */
    fun workingMillis(startMs: Long, endMs: Long, offsetMinutes: Int): Long {
        val day = 86_400_000L
        val s = startMs + offsetMinutes * 60_000L
        val e = endMs + offsetMinutes * 60_000L
        var total = 0L
        var midnight = s.floorDiv(day) * day
        while (midnight < e) {
            if (isWorkingDay(midnight / day)) total += maxOf(0L, minOf(e, midnight + WORK_END_HOUR * 3_600_000L) - maxOf(s, midnight + WORK_START_HOUR * 3_600_000L))
            midnight += day
        }
        return total
    }

    /** False on Saturdays, Sundays and South African public holidays. [epochDay] is days since 1970-01-01. */
    fun isWorkingDay(epochDay: Long): Boolean {
        if ((epochDay + 3).mod(7) >= 5) return false // 1970-01-01 was a Thursday; 5 = Saturday, 6 = Sunday
        val year = 1970 + (epochDay * 400).floorDiv(146_097L)
        return (year - 1..year + 1).none { epochDay in saHolidays(it) }
    }

    /**
     * South African public holidays in [year] as epoch days; one falling on a Sunday is also off on the Monday.
     * ponytail: fixed list, so once-off holidays the President declares (e.g. election days) still count as work days.
     */
    fun saHolidays(year: Long): Set<Long> {
        val easter = easterSunday(year)
        val fixed = listOf(1 to 1, 3 to 21, 4 to 27, 5 to 1, 6 to 16, 8 to 9, 9 to 24, 12 to 16, 12 to 25, 12 to 26).map { (m, d) -> epochDay(year, m, d) }
        return (fixed + fixed.filter { (it + 3).mod(7) == 6 }.map { it + 1 } + listOf(easter - 2, easter + 1)).toSet()
    }

    /** Gregorian Easter Sunday (anonymous algorithm) as an epoch day. */
    private fun easterSunday(y: Long): Long {
        val a = y % 19; val b = y / 100; val c = y % 100
        val h = (19 * a + b - b / 4 - (b - (b + 8) / 25 + 1) / 3 + 15) % 30
        val l = (32 + 2 * (b % 4) + 2 * (c / 4) - h - c % 4) % 7
        val m = (a + 11 * h + 22 * l) / 451
        return epochDay(y, ((h + l - 7 * m + 114) / 31).toInt(), ((h + l - 7 * m + 114) % 31 + 1).toInt())
    }

    /** Days since 1970-01-01 for a Gregorian date (Hinnant's days_from_civil). */
    private fun epochDay(year: Long, month: Int, day: Int): Long {
        val y = if (month <= 2) year - 1 else year
        val era = y.floorDiv(400L)
        val yoe = y - era * 400
        val doy = (153 * (if (month > 2) month - 3 else month + 9) + 2) / 5 + day - 1
        return era * 146_097 + yoe * 365 + yoe / 4 - yoe / 100 + doy - 719_468
    }

    /** "h:mm:ss" for a timer. */
    fun formatTimer(ms: Long): String {
        val seconds = maxOf(0L, ms) / 1000
        return "${seconds / 3600}:${(seconds / 60 % 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}"
    }

    /** Search box: true when every word of [query] appears somewhere in [fields], ignoring case. A blank query matches everything. */
    fun matches(query: String, fields: List<String>): Boolean {
        val text = fields.joinToString(" ").lowercase()
        return query.lowercase().split(' ', '\t', '\n').filter { it.isNotEmpty() }.all { it in text }
    }

    /**
     * Task attachments: `attachments` is a list of {url, storagePath, fileName, mimeType}, stored in Storage
     * under support/kanban/{projectId}/{uploadId}/{fileName} (storage.rules).
     */
    const val MAX_ATTACHMENTS: Int = 10

    fun attachmentPath(projectId: String, uploadId: String, fileName: String): String =
        "support/kanban/$projectId/$uploadId/${fileName.replace(Regex("[^A-Za-z0-9._-]"), "_").ifBlank { "attachment" }}"

    /** Images are shown as pictures; everything else as a file chip. */
    fun isImage(mimeType: String, fileName: String): Boolean =
        mimeType.startsWith("image/") || fileName.lowercase().substringAfterLast('.', "") in setOf("jpg", "jpeg", "png", "gif", "webp", "heic", "heif")

    /** The board columns to show for a status filter: just [selected], or every status when it is blank or not one of [statuses]. */
    fun shownStatuses(statuses: List<String>, selected: String): List<String> = if (selected in statuses) listOf(selected) else statuses

    fun normalizeProjectStatus(status: String): String = status.takeIf { it in projectStatuses } ?: "idea"
    fun normalizeTaskStatus(status: String): String = status.takeIf { it in taskStatuses } ?: "todo"
    fun normalizePriority(priority: String): String = priority.takeIf { it in priorities } ?: "medium"
}
